import qs from 'qs';
import port from '@config/port';
import { MD5 } from 'crypto-js';
import { navigateTo } from './router';
import systemInfo from '@/stores/systemInfo';
import { getPlatform, AES, toLogin } from './methods';
import type { service, method, params, reqData, resData, storageReqParams } from '@config/types';

export const md5 = (data: string) => MD5(data).toString();
const needAES = ['pwd', 'rePwd', 'safePwd', 'pwdRepeat'];
const noToLogin = [
	port.MEMBER_BINDMEMBER,
	port.MEMBER_GETMEMBERDETAIL,
	port.LIVE_GETLIVELIST,
	port.PAYMENT_QUERYMERCHANT,
];

const { VITE_API_BASE, VITE_API_SUFFIX } = import.meta.env;
export const apiRoot = `${VITE_API_BASE}${VITE_API_SUFFIX}`;

/** 等待请求响应队列 */
const reqQueue: any = new Map();
export const mergeReq = (params: params): Promise<any> => {
	return new Promise(async (resolve) => {
		const port = params.port;

		if (reqQueue.get(port)) {
			reqQueue.get(port).push(resolve);
		} else {
			reqQueue.set(port, [resolve]);
			let info: any;
			try {
				info = await request.get({ ...params, showLoad: false, resErr: true });
			} catch (error) {
				info = error;
			}
			reqQueue.get(port).forEach((fun) => fun(info));
			reqQueue.delete(port);
		}
	});
};

/** 缓存请求 */
export const storageReq = async (parameter: storageReqParams) => {
	const { port, params, callback } = parameter;
	let key = `port:${port},params:${JSON.stringify(params)}`;
	key = md5(key);

	let info = uni.getStorageSync(key);
	info && callback(info);

	info = await request.get({ port, params, showLoad: !info });
	uni.setStorageSync(key, info);
	callback(info);
};

export const setParams = async (params: any) => {
	let plat = getPlatform();
	const { token, plat: _plat, token2 } = systemInfo();

	plat = plat == 'h5' ? _plat : plat;

	params.sign && delete params.sign;
	params.header && delete params.header;

	params = {
		// plat: 'ios',
		plat: params.plat || plat,
		token: params.token || token || '',
		token2: params.token2 || token2 || '',
		...params,
	};

	let version = uni.getSystemInfoSync().appWgtVersion ?? UNI_VERSION;
	version = version?.replace('-test', '');
	params.version = version;

	for (const key in params) {
		needAES.includes(key) && params[key] && (params[key] = AES.encrypt(params[key]));
	}

	return params;
};

export const createSign = (params: any, service?: service) => {
	params = cloneDeep(params); //数据隔离
	service && (params.service = service);

	const arr = Object.keys(params).sort();
	let str = '';
	for (const key of arr) str += params[key];
	str += import.meta.env.VITE_SIGN_KEY;

	return md5(md5(md5(str)));
};

const toRequest = async <T>(reqData: reqData): Promise<resData<T>> => {
	const { method, port: service, params = {}, resErr = false, showLoad = true } = reqData;
	let cloneParams = cloneDeep(params);
	const header: any = cloneParams?.header || {
		'content-type': 'application/x-www-form-urlencoded',
	};

	showLoad && uni.showLoading({ mask: true });
	for (const key in cloneParams) cloneParams[key] === undefined && delete cloneParams[key];
	cloneParams = await setParams(cloneParams);
	cloneParams.sign = createSign(cloneParams, service);

	// 请求配置
	const config: any = {
		method,
		data: {} as any,
		url: `${apiRoot}?service=${service}`,
	};

	if (method == 'GET') {
		config.data = cloneParams;
	} else {
		config.header = header;
		config.data = { service, ...cloneParams };
	}
	try {
		const { data = {} }: any = await uni.request(config);
		showLoad && uni.hideLoading();
		// #ifdef APP
		console.log({ url: config.url, service, params: cloneParams, res: data.data });
		// #endif
		const responseData = data.data || {};
		const { code: responseCode, info, msg } = responseData;

		if (Number(data?.ret) === 200) {
			switch (Number(responseCode)) {
				case 0:
					if (resErr) {
						const resErrInfo: any = { code: responseCode, msg };
						if (info && typeof info === 'object') {
							for (const key in info) {
								if (Object.prototype.hasOwnProperty.call(info, key)) {
									resErrInfo[key] = info[key];
								}
							}
						}
						return resErrInfo;
					} else {
						return info || {};
					}

				case 1030043:
					// 获取当前用户ID
					const currentUserInfo = uni.getStorageSync('USER_INFO');
					const currentUserId = currentUserInfo?.mid;

					// 获取账户列表
					const sysInfo = systemInfo();
					const accounts = sysInfo.getAccountsList();

					// 直接从列表中删除失效账号
					const remainingAccounts = accounts.filter(
						(acc) => acc.userInfo?.mid && acc.userInfo?.mid !== currentUserId,
					);

					// 先保存更新后的列表
					sysInfo.saveAccountsList(remainingAccounts);
					sysInfo.safeSetToken('');

					if (remainingAccounts.length > 0) {
						// 有其他账号，选择最近使用的账号
						const nextAccount = remainingAccounts.reduce((prev, curr) =>
							prev.timestamp > curr.timestamp ? prev : curr,
						);

						// 确保新账号是当前账号
						sysInfo.setAccountCurrent(nextAccount.userInfo.mid);

						// 设置用户信息
						uni.setStorageSync('USER_INFO', nextAccount.userInfo);

						// 安全设置token（不触发API调用）
						sysInfo.safeSetToken(nextAccount.token);

						// 显示提示
						uni.showToast({
							title: '当前账号已失效，已自动切换到其他账号',
							icon: 'none',
							duration: 2000,
						});

						// 关闭所有页面并跳转到主页
						setTimeout(() => {
							uni.reLaunch({
								url: '/pages/home',
							});
						}, 500);

						return Promise.reject({ code: 1030043, msg: '账号已失效' });
					} else {
						// 没有其他可用账号，执行原有逻辑
						!noToLogin.includes(service) && toLogin();
					}
					break;

				case 1050043:
					systemInfo().token2 = '';
					navigateTo('/pages/shop/login', 'replace');
					break;

				case 1030055:
					confirmModal('请先实名认证', () => {
						navigateTo('/pages/services/cert');
					});
					break;

				default:
					if (!resErr) {
						uni.showToast({ title: msg, icon: 'none' });
					} else {
						const errorInfo: any = { code: responseCode, msg };
						if (info && typeof info === 'object') {
							for (const key in info) {
								if (Object.prototype.hasOwnProperty.call(info, key)) {
									errorInfo[key] = info[key];
								}
							}
						}
						return errorInfo;
					}
			}

			// 创建拒绝用的错误信息
			const rejectInfo: any = { code: responseCode, msg };
			if (info && typeof info === 'object') {
				for (const key in info) {
					if (Object.prototype.hasOwnProperty.call(info, key)) {
						rejectInfo[key] = info[key];
					}
				}
			}
			return Promise.reject(rejectInfo);
		} else {
			if (!resErr) uni.showToast({ title: '网络请求失败', icon: 'none' });
			return Promise.reject({ ...data, info: {}, code: data.ret });
		}
	} catch (error: any) {
		console.error(error);
		showLoad && uni.hideLoading();
		if (!resErr) uni.showToast({ title: error?.errMsg || '网络请求失败', icon: 'none' });
		return Promise.reject(error);
	}
};

const request = {
	async get<T = Record<string, any>>(port: service | params, params?: any) {
		return await this.request<T>({ method: 'GET', port, params });
	},
	async post<T = Record<string, any>>(port: service | params, params?: any) {
		return await this.request<T>({ method: 'POST', port, params });
	},
	async request<T>(parameter: { method: method; port: service | params; params: any }) {
		const { method, port, params } = parameter;
		const reqData: reqData =
			typeof port == 'string' ? { method, port, params } : { method, ...port };

		try {
			return await toRequest<T>(reqData);
		} catch (error) {
			return Promise.reject(error);
		}
	},
	upload(filePath: string, onProgressUpdate = () => {}) {
		return new Promise(async (resolve, reject) => {
			const service = port.SYSTEM_UPLOAD;
			let formData: any = { service };

			formData = await setParams(formData);
			formData.sign = createSign(formData);

			const uploadTask = uni.uploadFile({
				name: 'file',
				filePath,
				url: apiRoot + qs.stringify(formData, { addQueryPrefix: true }),
				success: (e) => {
					const data = JSON.parse(e.data);

					const url = data.data.url;
					url ? resolve(url) : reject();
				},
				fail: reject,
			});

			uploadTask.onProgressUpdate(onProgressUpdate);
		});
	},
};

export default request;
